/**
 * Chat sessions grouped by recency, and the words each row carries.
 * Shared by the Workshop's session switcher and the M7 rail's history view.
 */
import type { ChatSession } from "@/types/chat.type";
import { focusLabel } from "@/app/routes/dashboard/agent/focusLabel";

export type SessionGroupKey = "today" | "yesterday" | "week" | "earlier";

export const SESSION_GROUPS: Array<{ key: SessionGroupKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "Past 7 days" },
  { key: "earlier", label: "Earlier" },
];

export const sessionTimeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});

export const sessionWeekdayFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
});

export const sessionDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});

export const sessionDateWithYearFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

export const sessionFullDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function sessionDate(session: ChatSession): Date | null {
  const lastMessage = new Date(session.lastMessageAt);
  if (!Number.isNaN(lastMessage.getTime())) return lastMessage;

  const created = new Date(session.createdAt);
  return Number.isNaN(created.getTime()) ? null : created;
}

export function startOfDay(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

export function sessionGroup(session: ChatSession, now: Date): SessionGroupKey {
  const date = sessionDate(session);
  if (!date) return "earlier";

  const difference = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (difference <= 0) return "today";
  if (difference === 1) return "yesterday";
  if (difference <= 7) return "week";
  return "earlier";
}

export function sessionWhen(session: ChatSession, group: SessionGroupKey, now: Date): string {
  const date = sessionDate(session);
  if (!date) return "Date unavailable";
  if (group === "today" || group === "yesterday") return sessionTimeFormatter.format(date);
  if (group === "week") return sessionWeekdayFormatter.format(date);
  return date.getFullYear() === now.getFullYear()
    ? sessionDateFormatter.format(date)
    : sessionDateWithYearFormatter.format(date);
}

export function sessionSurface(session: ChatSession): string {
  return session.lastFocus ? focusLabel(session.lastFocus, true) : "Creo";
}
